import React from 'react';
import { Box, Link, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

// States that need the operator: an amber dot before the label
export const NEEDS_ACTION = new Set(['future', 'otherLane', 'notDone', 'inactive']);
const AMBER = '#f59e0b';
const INK = '#37352f';
const GREY = '#787774';
const FONT = '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif';

/** A 'YYYY-MM-DD' day as "13 oct", with the year only when it is not this year. */
export const formatDay = (iso, lang) => {
  if (!iso) return '';
  const [year, month, day] = iso.split('-').map(Number);
  const yearPart = year !== new Date().getFullYear() ? { year: 'numeric' } : {};
  return new Date(year, month - 1, day).toLocaleDateString(lang, { day: 'numeric', month: 'short', ...yearPart });
};

/**
 * A running experiment in words: is the change in its lane, since when it counts, and what it sold against what the
 * lane used to sell in as many days. When it is not counting yet, it says why. Given `children` (a table's status
 * indicator) it shows them instead, with the words in their tooltip and an amber dot when the operator is needed.
 */
const MeasurementStatus = ({ measurement, units, baselineUnits, zeroBaseline, lanes, machineId, children }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const lang = i18n.language || 'es';
  const m = measurement;
  const day = (iso) => formatDay(iso, lang);
  const measured = (lanes || []).join(', ');
  const where = measured ? t('ms_inLane', { lanes: measured }) : t('ms_inMachine');
  const sold = Math.round(units || 0);
  // A running experiment's baseline comes pro-rated to the days counted: what the lane sold in as many days
  const expected = baselineUnits ?? null;
  // A real zero (the product was in the lane and did not sell) is not missing history
  const expectedText = zeroBaseline
    ? t('ms_soldNothingBefore')
    : expected == null
    ? t('ms_noHistory')
    : expected < 1 ? t('ms_expectedUnderOne') : t('ms_expected', { count: Math.round(expected) });
  const soldLine = `${t('ms_sold', { count: sold })} · ${expectedText}`;
  const planned = t('ms_planned', { date: day(m.plannedStart) });

  const { label, detail, hint } = {
    measuring: {
      label: t('ms_measuring', { date: day(m.since) }),
      detail: soldLine,
      hint: zeroBaseline
        ? t('ms_measuringZeroHint', { count: m.dataDays, sold })
        : expected == null
        ? t('ms_measuringNoHistoryHint', { count: m.dataDays, sold })
        : t('ms_measuringHint', { count: m.dataDays, sold, expected: expected.toLocaleString(lang, { maximumFractionDigits: 1 }) }),
    },
    // Not active on Myma (no_sales_machines): the machine master says why and is where to fix it
    inactive: {
      label: m.inactiveReason === 'no_planogram'
        ? t('ms_inactiveNoPlanogram')
        : m.lastSale ? t('ms_noData', { date: day(m.lastSale) }) : t('ms_noDataNever'),
      detail: (
        <Link
          component="button"
          underline="hover"
          onClick={(e) => { e.stopPropagation(); navigate('/settings/clients', { state: { machineSearch: machineId } }); }}
          sx={{ fontSize: '11px', color: children ? '#93c5fd' : '#2563eb', verticalAlign: 'baseline' }}
        >
          {t('ms_inactiveLink')}
        </Link>
      ),
      hint: t('ms_inactiveHint', {
        reason: {
          no_sales: t('ms_inactiveReasonNoSales'),
          no_live_sales: t('ms_inactiveReasonNoLiveSales'),
          no_planogram: t('ms_inactiveReasonNoPlanogram'),
        }[m.inactiveReason] || t('ms_inactiveReasonNoSales'),
      }),
    },
    waiting: { label: t('ms_waiting'), detail: planned, hint: t('ms_waitingHint', { where }) },
    notDone: { label: t('ms_notDone'), detail: planned, hint: t('ms_notDoneHint', { date: day(m.plannedStart), where }) },
    otherLane: {
      label: t('ms_otherLane', { lanes: m.otherLanes.join(', ') }),
      detail: measured ? t('ms_measuresLane', { lanes: measured }) : planned,
      hint: t('ms_otherLaneHint', { other: m.otherLanes.join(', '), lanes: measured }),
    },
    future: { label: t('ms_future', { date: day(m.plannedStart) }), detail: t('ms_checkDate'), hint: t('ms_futureHint', { date: day(m.plannedStart) }) },
  }[m.state] || {};

  if (children) {
    return (
      <Tooltip
        arrow
        placement="top"
        componentsProps={{ tooltip: { sx: { bgcolor: '#1e293b', maxWidth: 300, '& .MuiTooltip-arrow': { color: '#1e293b' } } } }}
        title={(
          <Box onClick={(e) => e.stopPropagation()} sx={{ p: 0.25, fontFamily: FONT }}>
            <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#f1f5f9' }}>{label}</Typography>
            <Typography component="div" sx={{ fontSize: '11px', color: '#cbd5e1' }}>{detail}</Typography>
            {hint && <Typography sx={{ fontSize: '11px', color: '#94a3b8', mt: 0.5 }}>{hint}</Typography>}
          </Box>
        )}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px', width: '100%', cursor: 'help' }}>
          {NEEDS_ACTION.has(m.state) && (
            <Box component="span" data-needs-action sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: AMBER, flexShrink: 0 }} />
          )}
          <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
        </Box>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={hint || ''} arrow placement="top">
      <Box sx={{ textAlign: 'left', width: '100%', minWidth: 0, cursor: 'help', fontFamily: FONT, letterSpacing: '-0.01em' }}>
        <Typography noWrap sx={{ fontSize: '12px', fontWeight: 500, color: INK, display: 'flex', alignItems: 'center', gap: '5px' }}>
          {NEEDS_ACTION.has(m.state) && <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: AMBER, flexShrink: 0 }} />}
          <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</Box>
        </Typography>
        <Typography noWrap sx={{ fontSize: '11px', color: GREY }}>{detail}</Typography>
      </Box>
    </Tooltip>
  );
};

export default MeasurementStatus;
